import { useEffect, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useController, useWatch } from 'react-hook-form';

import DraggableList from '../../../../../../components/DraggableList/DraggableList';
import { RULES, SUB_TOPIC_ID } from '../../../../../../constants/formFields';

import type {
  ISurveyAnswerPrioritiseTopicProps,
  PrioritizedSubTopic,
  PriorityItem,
} from '../interfaces/ISurveyAnswerPrioritiseTopic';

import styles from './scss/PrioritiseCheckboxList.module.scss';

function PrioritiseDragCheckboxList({
  control,
  standardTopicsData,
}: ISurveyAnswerPrioritiseTopicProps) {
  const selectedSubTopics = useWatch({
    control,
    name: SUB_TOPIC_ID,
    defaultValue: [],
  });

  const {
    field: {
      value: rulesValue,
      onChange: onRulesChange,
    },
  } = useController({
    control,
    name: RULES,
    defaultValue: [],
  });

  const basePriorityItems = useMemo((): PriorityItem[] => {
    const items: PriorityItem[] = [];

    if (standardTopicsData && standardTopicsData?.length > 0) {
      standardTopicsData.forEach((topic) => {
        topic.children.forEach((subTopic) => {
          if (Array.isArray(selectedSubTopics)
            && selectedSubTopics.some((item) => item.subTopicId === subTopic.id)) {
            items.push({
              id: subTopic.id,
              position: 0,
              topicTitle: topic.title,
              subTopicTitle: subTopic.title,
              uniqueId: `${topic.id}-${subTopic.id}`,
              standardName: topic.standard.name,
              topicId: topic.id,
            });
          }
        });
      });
    }

    return items;
  }, [selectedSubTopics, standardTopicsData]);

  const prioritizedItems = useMemo((): PriorityItem[] => {
    const priorityValue = Array.isArray(rulesValue) ? rulesValue : [];

    if (priorityValue.length === 0) {
      return basePriorityItems.map((item, idx) => ({
        ...item,
        position: idx + 1,
      }));
    }

    return priorityValue.map((item: PrioritizedSubTopic, idx: number) => {
      const foundItem = basePriorityItems.find((baseItem) => baseItem.id === item.subTopicId);

      return foundItem ? {
        ...foundItem,
        position: idx + 1,
      } : null;
    }).filter(Boolean) as PriorityItem[];
  }, [basePriorityItems, rulesValue]);

  useEffect(() => {
    const priorityValue = Array.isArray(rulesValue) ? rulesValue : [];

    if (priorityValue.length > 0 || basePriorityItems.length === 0) {
      return;
    }

    const initialValue: PrioritizedSubTopic[] = basePriorityItems.map((item, idx) => ({
      subTopicId: Number(item.id),
      topicId: Number(item.topicId),
      position: idx + 1,
    }));

    onRulesChange(initialValue);
  }, [basePriorityItems, onRulesChange, rulesValue]);

  const handleMove = (dragIndex: number, hoverIndex: number) => {
    const newItems = [...prioritizedItems];
    const draggedItem = newItems[dragIndex];
    newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, draggedItem);

    const updatedValue: PrioritizedSubTopic[] = newItems.map((item, idx) => ({
      subTopicId: Number(item.id),
      topicId: Number(item.topicId),
      position: idx + 1,
    }));

    onRulesChange(updatedValue);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={styles.container}>
        <DraggableList
          items={prioritizedItems}
          onMove={handleMove}
          showNumber
          renderItem={(item) => (
            <div className={styles.itemContent}>
              <div className={styles.textContent}>
                <div className={styles.topicTitle}>
                  <span>
                    <strong>
                      {item.standardName}
                      {' '}
                      Topic
                    </strong>
                    {' '}
                    -
                    {' '}
                    {item.topicTitle}
                  </span>
                </div>
                <div className={styles.subTopicTitle}>
                  {item.subTopicTitle}
                </div>
              </div>
            </div>
          )}
          itemClassName={styles.prioritiseItem}
          itemType="PRIORITY_SUB_TOPIC"
          keyExtractor={(item: PriorityItem) => item.uniqueId}
        />
      </div>
    </DndProvider>
  );
}

export default PrioritiseDragCheckboxList;
